<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-medium"
    title="個別サービス設定"
  >
    <template v-slot:body="{ cancel }">
      <validation-observer v-slot="{ handleSubmit }" as="div">
        <v-form novalidate @submit.stop.prevent="handleSubmit(submit)">
          <!-- BODY -->
          <v-card-text class="pa-0">
            <div class="modal-body">
              <v-container>
                <!-- 名前 -->
                <v-row>
                  <v-col>
                    <app-badge
                      item="名前"
                      :msg="
                        $filter.message('info_alpha_num', { maxLength: '63' })
                      "
                      placement="end"
                    />
                    <validation-provider
                      name="serviceName"
                      v-model="serviceSettingInfo.serviceName"
                      label="名前"
                      :rules="{
                        required: true,
                        max: { length: 63 },
                        is_half_width_alpha_numeric: true,
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
                <!-- プロトコル -->

                <v-row>
                  <v-col>
                    <app-badge
                      item="プロトコル"
                      :isRequired="false"
                      :isInfo="false"
                    />
                    <v-radio-group
                      inline
                      v-model="serviceSettingInfo.protocol"
                      :options="protocolOptions"
                      hide-details
                    >
                      <v-radio
                        v-for="option in protocolOptions"
                        :key="option"
                        :label="option"
                        :value="option"
                      />
                    </v-radio-group>
                  </v-col>
                </v-row>
                <!-- Port -->
                <v-row>
                  <v-col>
                    <app-badge
                      item="Port"
                      :msg="$filter.message('info_custom_port')"
                      placement="end"
                    />
                    <validation-provider
                      name="portNumber"
                      v-model="serviceSettingInfo.portNumber"
                      label="Port"
                      :rules="{
                        required: true,
                        is_custom_port: true,
                        is_custom_port_length: { length: 10 },
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
                <!-- 備考 -->
                <v-row>
                  <v-col>
                    <app-badge
                      item="備考"
                      :msg="
                        $filter.message('info_max_length', { maxLength: '20' })
                      "
                      :isRequired="false"
                      placement="end"
                    />
                    <validation-provider
                      name="description"
                      v-model="serviceSettingInfo.description"
                      label="備考"
                      :rules="{
                        max: { length: 20 },
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
              </v-container>
            </div>
          </v-card-text>

          <v-divider></v-divider>
          <!-- FOOTER -->
          <v-card-actions>
            <v-list-item class="w-100 pa-1">
              <template v-slot:append>
                <v-btn type="submit" color="primary" variant="flat">{{
                  serviceSetting ? "変更" : "追加"
                }}</v-btn>
                <v-btn @click="cancel" variant="outlined" color="primary"
                  >キャンセル</v-btn
                >
              </template>
            </v-list-item>
          </v-card-actions>
        </v-form>
      </validation-observer>
    </template>
  </app-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import { VueFinalModal } from "vue-final-modal";
import type { CustomService, CustomServiceInfo } from "@/apis/CustomServiceApi";
import cloneDeep from "lodash/cloneDeep";

export default defineComponent({
  name: "ServiceSettingEdit",
  props: {
    serviceSetting: {
      type: Object as PropType<CustomServiceInfo>,
      required: false,
      default: null,
    },
    customServiceKeyId: {
      type: String as PropType<string>,
      required: true,
    },
    type4Id: {
      type: String as PropType<string>,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      serviceSettingInfo: { protocol: "TCP" } as CustomServiceInfo,
      protocolOptions: ["TCP", "UDP"],
    };
  },
  mounted() {
    // 更新であればフォームのデータを設定
    if (this.serviceSetting) {
      this.serviceSettingInfo = cloneDeep(this.serviceSetting);
    }
  },
  methods: {
    /** サービス設定の登録・変更 */
    async submit() {
      // 可変のメッセージを設定
      const action = this.serviceSetting ? "変更" : "追加";

      // 確認ダイアログの表示
      await this.$confirm(`個別サービスを${action}します。よろしいですか？`);

      // 登録・更新データの設定
      const reqData: CustomService = {
        serviceName: this.serviceSettingInfo.serviceName,
        protocol: this.serviceSettingInfo.protocol,
        portNumber: this.serviceSettingInfo.portNumber,
        description: this.serviceSettingInfo.description || null,
        customServiceKeyId: this.customServiceKeyId,
        note: null,
      };

      // サービス設定の登録・更新
      if (this.serviceSetting) {
        await this.modService(reqData);
      } else {
        await this.addService(reqData);
      }
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
    /**
     * サービス設定登録APIを呼び出す
     * @param reqData 登録データ
     */
    async addService(reqData: CustomService) {
      // Type4IDの有無により呼び出すAPIをハンドリング
      if (!this.type4Id) {
        await this.$api.customServiceApi.postType1CustomService(reqData);
      } else {
        await this.$api.customServiceApi.postType4CustomService(
          this.type4Id,
          reqData
        );
      }
    },
    /**
     * サービス設定変更APIを呼び出す
     * @param reqData 変更データ
     */
    async modService(reqData: CustomService) {
      // Type4IDの有無により呼び出すAPIをハンドリング
      if (!this.type4Id) {
        await this.$api.customServiceApi.putType1CustomService(
          this.serviceSettingInfo.customServiceSeq,
          reqData
        );
      } else {
        await this.$api.customServiceApi.putType4CustomService(
          this.type4Id,
          this.serviceSettingInfo.customServiceSeq,
          reqData
        );
      }
    },
  },
});
</script>
